import {
    Button,
    Hooks,
    Popup,
    ShareButton,
    Utils,
    ReportCompanyModal
} from '@microfrontends/react-components'
import Image from 'next/image'
import { useCallback, useEffect, useRef, useState } from 'react'
import { SelectTab } from '../CompanyDetail'
import { useStore } from '../../store'
import { ICompanyInfo } from '../CompanyDetail/type'
import {
    Avatar,
    ContentWrapper,
    BackgroundContainer,
    MainBackgroundImage,
    MainContent,
    CompanyInfo,
    FollowAction,
    CompanyName,
    TabAndShare,
    TabSection,
    TabItem,
    CompanyAddress,
    TabAndShareWrapper,
    TabSectionWrapper,
    ContanerWithBg
} from './Banner.css'
import { companyFollow } from '../../services/companyDetailService'
import { EMPTY_COMPANY_IMAGE } from '../constants'
import { DEFAULT_SUCCESS_CODE } from '../../services/jobService'
import Head from 'next/head'
import { showLoginModal } from '../../helpers/common'

interface IBannerProps {
    companyInfo: ICompanyInfo | null
    t: (text: string) => string
    selectedTab: SelectTab
    onTabChange: (tab: SelectTab) => void
    companyBannerImage: string
}

interface IStickyDataState {
    stopDetectFixedHeader: boolean
    fixedOffset: number
    bannerBarShortHeight: number
}

// const NAV_BAR_HEIGHT = 80
// const NAV_BAR_HEIGHT_MOBILE = 66

const NAV_BAR_HEIGHT = 0
const NAV_BAR_HEIGHT_MOBILE = 64

const Banner = (props: IBannerProps) => {
    const { t, companyInfo, selectedTab, onTabChange, companyBannerImage } = props
    const { langCode, isMobile, user } = useStore().state
    // Sticky banner
    const [stickyData, setStickyData] = useState<IStickyDataState>({
        stopDetectFixedHeader: true,
        fixedOffset: 0,
        bannerBarShortHeight: isMobile ? 190 : 250 // Fixed header height
    })
    // Follow
    const [isFollowed, setFollowed] = useState<boolean>(companyInfo !== null ? companyInfo.isFollowed === 1 : false)
    const [followCount, setFollowCount] = useState<number>(companyInfo !== null ? companyInfo.totalFollow : 1)
    // Unfollow dialog
    const [isShowUnfollowDialog, setShowUnfollowDialog] = useState<boolean>(false)

    const mainBannerRef = useRef<HTMLDivElement>(null)
    const tabBarWrapperRef = useRef<HTMLDivElement>(null)
    const tabBarRef = useRef<HTMLDivElement>(null)
    const tabSectionWrapperRef = useRef<HTMLDivElement>(null)
    const tabSectionRef = useRef<HTMLDivElement>(null)

    const data = {
        apiGetway: process.env.NEXT_PUBLIC_MS_BASE_PATH + '/company-profile/v1.0',
        apiTokenAccess: user?.accessToken || '',
        isAuthenticated: user !== null && user.accessToken ? 1 : 0,
        userId: user?.userId || 0,
        isReportCompany: !!companyInfo?.isReported,
        imagesService: process.env.NEXT_PUBLIC_IMAGE_URL || '',
        company: {
            companyName: companyInfo?.companyName || '',
            companyLogo: companyInfo?.companyLogoURL || '',
            companyId: companyInfo?.companyId || 0
        }
    }

    const doFollow = useCallback(
        async (isFollowed: boolean) => {
            if (user === null || !user.accessToken) {
                return
            }
            if (companyInfo === null || !companyInfo.companyId) {
                return
            }
            const response = await companyFollow(companyInfo.companyId, isFollowed, user?.accessToken, langCode)
            if (response?.code !== DEFAULT_SUCCESS_CODE) {
                return
            }
            let toastTitle = t('Unfollowed')
            let toastMessage = t("You'll stop receive the newest jobs from this company.")
            if (isFollowed) {
                toastTitle = t('Yay! Thanks for following')
                toastMessage = t('You will receive the newest jobs from this company.')
            }
            Utils.showToast(toastTitle, toastMessage, 'success')
        },
        [companyInfo, langCode, t, user]
    )

    const changeFollowState = useCallback(
        (followed: boolean) => {
            setFollowed(followed)
            setFollowCount(followed ? followCount + 1 : followCount - 1)
            // Call API to update follow
            doFollow(followed)
        },
        [doFollow, followCount]
    )

    const onClickFollowUs = useCallback(() => {
        if (user === null || !user.accessToken) {
            showLoginModal(langCode)
            return
        }
        if (companyInfo === null || !companyInfo.companyId) {
            return
        }
        const followed = !isFollowed
        if (!followed) {
            setShowUnfollowDialog(true)
        } else {
            changeFollowState(followed)
        }
    }, [changeFollowState, companyInfo, isFollowed, langCode, user])

    const onClickTabItem = useCallback(
        (tab: SelectTab, tabName: string) => {
            const element = document.getElementById(tabName)

            onTabChange(tab)
            if (element === undefined || element === null) {
                return
            }
        },
        [onTabChange]
    )

    const tabSectionScrollTo = useCallback((position: number) => {
        if (tabSectionRef && tabSectionRef.current) {
            tabSectionRef.current.scrollTo({ left: position, behavior: 'smooth' })
        }
    }, [])

    const onClickConfirmUnfollow = useCallback(() => {
        changeFollowState(false)
        setShowUnfollowDialog(false)
    }, [changeFollowState])

    useEffect(() => {
        if (tabSectionRef.current) {
            const tabSectionChildren = tabSectionRef.current.childNodes
            const left = (tabSectionChildren[selectedTab] as HTMLElement).getBoundingClientRect().left
            tabSectionScrollTo(left)
        }
    }, [selectedTab, tabSectionScrollTo])

    useEffect(() => {
        // Fixed Banner full height
        const handleScrolling = () => {
            setStickyData({
                ...stickyData,
                fixedOffset: window.scrollY
            })
            if (
                mainBannerRef &&
                mainBannerRef.current &&
                tabBarRef &&
                tabBarRef.current &&
                tabBarWrapperRef &&
                tabBarWrapperRef.current
            ) {
                const tabBarHeight = tabBarRef.current.getBoundingClientRect().height
                const mainBannerElement = mainBannerRef.current
                const { bottom } = mainBannerElement.getBoundingClientRect()
                const navBarHeight = isMobile ? NAV_BAR_HEIGHT_MOBILE : NAV_BAR_HEIGHT
                const mainBannerPosition = bottom - tabBarHeight - navBarHeight
                if (mainBannerPosition <= 0) {
                    tabBarRef.current.classList.add('sticky')
                    tabBarWrapperRef.current.classList.add('sticky')
                    mainBannerElement.style.paddingBottom = `${tabBarHeight}px`
                } else {
                    tabBarRef.current.classList.remove('sticky')
                    tabBarWrapperRef.current.classList.remove('sticky')
                    mainBannerElement.style.paddingBottom = 'unset'
                }
            }
        }
        window.addEventListener('scroll', handleScrolling, { passive: true })
        return () => {
            window.removeEventListener('scroll', handleScrolling)
        }
    }, [isMobile, stickyData])

    useEffect(() => {
        if (companyInfo !== null) {
            setFollowed(companyInfo !== null ? companyInfo.isFollowed === 1 : false)
            setFollowCount(companyInfo !== null ? companyInfo.totalFollow : 1)
        }
    }, [companyInfo])
    const [setRef, mainBannerSize] = Hooks.useElementSize()

    return (
        <ContentWrapper
            ref={(node) => {
                ;(mainBannerRef.current as HTMLDivElement | null) = node
                setRef(node)
            }}
            id='bannerSection'
        >
            <Head>
                <link rel='preload' as='image' href={companyBannerImage} />
            </Head>
            <ContanerWithBg>
                <BackgroundContainer>
                    <MainBackgroundImage>
                        <Image src={companyBannerImage} alt={companyInfo?.companyName ?? ''} priority layout='fill' />
                    </MainBackgroundImage>
                </BackgroundContainer>
                <MainContent>
                    <Avatar>
                        <div
                            style={{
                                backgroundImage: `url('${
                                    companyInfo && companyInfo.companyLogoURL !== ''
                                        ? companyInfo.companyLogoURL
                                        : EMPTY_COMPANY_IMAGE
                                }')`,
                                backgroundSize: 'contain',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'center',
                                display: 'block',
                                width: '100%',
                                height: '100%'
                            }}
                        />
                    </Avatar>
                    <CompanyInfo>
                        <CompanyName>{companyInfo?.companyName ?? ''}</CompanyName>
                        <CompanyAddress isNoMarginBottom>
                            {followCount} {followCount > 1 ? t('followers') : t('follower')}
                        </CompanyAddress>
                    </CompanyInfo>
                    <FollowAction isMobile={isMobile}>
                        <Button type='primary' isOutline={isFollowed} onClick={onClickFollowUs}>
                            {isFollowed ? t('Following') : t('Follow')}
                        </Button>

                        {!isMobile && (
                            <ReportCompanyModal
                                language={langCode}
                                data={data}
                                useGlobalToast={true}
                                locationClassName={''}
                            />
                        )}

                        {!isMobile && <ShareButton langCode={langCode} position='right' buttonStyle={{ padding: 8 }} />}

                        {isMobile && (
                            <ReportCompanyModal
                                language={langCode}
                                data={data}
                                useGlobalToast={false}
                                locationClassName={''}
                            />
                        )}

                        {isMobile && (
                            <ShareButton langCode={langCode} position='right' buttonStyle={{ padding: 8, width: 40 }} />
                        )}
                    </FollowAction>
                </MainContent>
                <TabAndShareWrapper ref={tabBarWrapperRef}>
                    <TabAndShare ref={tabBarRef} offset={NAV_BAR_HEIGHT}>
                        <TabSectionWrapper ref={tabSectionWrapperRef} width={mainBannerSize.width - 2}>
                            <TabSection ref={tabSectionRef}>
                                <TabItem
                                    id='AboutUs-tab'
                                    isSelected={selectedTab === SelectTab.ABOUT_US}
                                    onClick={() => onClickTabItem(SelectTab.ABOUT_US, 'AboutUs')}
                                >
                                    {t('About Us')}
                                </TabItem>
                                <TabItem
                                    id='OpeningJob-tab'
                                    isSelected={selectedTab === SelectTab.OPENING_JOB}
                                    onClick={() => onClickTabItem(SelectTab.OPENING_JOB, 'OpeningJob')}
                                >
                                    {t('Opening Jobs')}
                                </TabItem>
                            </TabSection>
                        </TabSectionWrapper>
                    </TabAndShare>
                </TabAndShareWrapper>
            </ContanerWithBg>

            {isShowUnfollowDialog && (
                <Popup
                    isShow={isShowUnfollowDialog}
                    lang={langCode}
                    styleOption={{ size: 'sm' }}
                    text={{
                        header: t('Unfollow company?'),
                        primaryBtn: t('Unfollow')
                    }}
                    onClose={() => setShowUnfollowDialog(false)}
                    onClickPrimary={onClickConfirmUnfollow}
                >
                    {t('You will not able to received notifications from this company.')}
                </Popup>
            )}
        </ContentWrapper>
    )
}

export default Banner
